import React, { useMemo } from "react";
import { Flex, Form } from "antd";
import { useTranslate } from "@refinedev/core";
import { Grid, Toggle } from "@libertify/ui";
import { PlaybookSelect } from "../../PlaybookSelect";
import { PlaybookFormItem } from "../../PlaybookFormItem";
import { PODCAST_SPEAKER_ROLES } from "../defaults";
import { useGenerationFieldOptions } from "../hooks/useGenerationFieldOptions";
import type { VoiceOption } from "../hooks/useAudioConfigData";
import type { PodcastFormValues } from "../types";
import { usePreviewPlayer } from "./usePreviewPlayer";
import { VoicePreviewButton } from "./VoicePreviewButton";

const VOICES_KEY = "createExperience.addOns.podcast.voices";

/** Stable identity so the role fields don't re-memo while no voice is picked. */
const NO_VOICES: string[] = [];

export interface PodcastSettingsFieldsProps {
  /** Selectable voices (an org avatar's voice profile). */
  voiceOptions: VoiceOption[];
  voicesLoading?: boolean;
  /** Seeds the role-exclusion check before the form has registered a watched
   *  value (mirrors the caller's own `initialValues`). */
  initialVoiceProfileIds?: string[];
  /** Leaves out the target-duration select. The form's own value for it is
   *  then not reported, so the caller must carry the stored value itself. */
  hideTargetDuration?: boolean;
}

interface VoiceOptionLabelProps {
  label: React.ReactNode;
  previewUrl: string | null;
  playing: boolean;
  onToggle: (url: string) => void;
}

/** A voice option's dropdown row: its label, plus a preview button when the
 *  voice has a sample to play. */
const VoiceOptionLabel: React.FC<VoiceOptionLabelProps> = ({
  label,
  previewUrl,
  playing,
  onToggle,
}) => (
  <Flex align="center" justify="space-between" gap={8}>
    <span>{label}</span>
    {previewUrl ? (
      <VoicePreviewButton
        previewUrl={previewUrl}
        playing={playing}
        onToggle={() => onToggle(previewUrl)}
      />
    ) : null}
  </Flex>
);

/**
 * Podcast settings fields, shared by the create-flow card and the edit-page
 * form so both render the exact same fields. Renders into the caller's
 * `<Form>` — create writes to the draft context, edit PATCHes the project.
 *
 * The backend requires exactly two voices in `voice_profile_ids`, positionally
 * assigned to the speakers, so this renders one select per role
 * ([[PODCAST_SPEAKER_ROLES]]) bound to its own array index instead of a single
 * free-for-all multi-select.
 */
export const PodcastSettingsFields: React.FC<PodcastSettingsFieldsProps> = ({
  voiceOptions,
  voicesLoading,
  initialVoiceProfileIds,
  hideTargetDuration = false,
}) => {
  const t = useTranslate();
  const { durations } = useGenerationFieldOptions();
  const form = Form.useFormInstance<PodcastFormValues>();
  const { playingUrl, toggle, stop } = usePreviewPlayer();

  const selectedVoiceIds =
    Form.useWatch(["generation_settings", "voice_profile_ids"], form) ??
    initialVoiceProfileIds ??
    NO_VOICES;

  const roleFields = useMemo(
    () =>
      PODCAST_SPEAKER_ROLES.map((role, index) => {
        const currentId = selectedVoiceIds[index];
        const options = voiceOptions.map((option) => ({
          ...option,
          // A voice can only take one role — grey out the one the other role
          // holds (two roles, so its index is `1 - index`). Skipped when the
          // org has a single voice, or the second role would have nothing left
          // to pick and the required pair could never be completed.
          disabled:
            voiceOptions.length > 1 &&
            option.value === selectedVoiceIds[1 - index],
        }));
        // A stored id with no matching option (e.g. a default playbook's
        // fixed voice_profile_ids, seeded outside this org's own contacts)
        // has no label to show — antd would otherwise fall back to printing
        // the raw id. Stand a placeholder option in for it instead.
        if (currentId && !options.some((option) => option.value === currentId)) {
          options.unshift({
            value: currentId,
            label: t(`${VOICES_KEY}.${role}.default`),
            previewUrl: null,
            disabled: true,
          });
        }
        return { role, index, labelKey: `${VOICES_KEY}.${role}.label`, options };
      }),
    [voiceOptions, selectedVoiceIds, t]
  );

  return (
    <Grid columns={2} gap="lg">
      {roleFields.map(({ role, index, labelKey, options }) => (
        <PlaybookFormItem
          key={role}
          name={["generation_settings", "voice_profile_ids", index]}
          labelKey={labelKey}
          rules={[{ required: true, message: t(`${VOICES_KEY}.required`) }]}
        >
          <PlaybookSelect
            options={options}
            loading={voicesLoading}
            placeholder={t(`${VOICES_KEY}.placeholder`)}
            onOpenChange={(open) => {
              if (!open) stop();
            }}
            optionRender={(option) => (
              <VoiceOptionLabel
                label={option.label}
                previewUrl={option.data.previewUrl}
                playing={playingUrl === option.data.previewUrl}
                onToggle={toggle}
              />
            )}
          />
        </PlaybookFormItem>
      ))}
      {/* TODO: not in the Podcast API contract — restore if the backend
          adds voice mood / format / language fields.
      <PlaybookFormItem
        name="format"
        labelKey="createExperience.addOns.podcast.format.label"
      >
        <PlaybookSelect options={formatOptions} />
      </PlaybookFormItem>
      <PlaybookFormItem
        name="voice"
        labelKey="createExperience.addOns.podcast.voice.label"
      >
        <PlaybookSelect options={voiceOptions} />
      </PlaybookFormItem>
      <PlaybookFormItem
        name="language"
        labelKey="createExperience.addOns.podcast.language.label"
      >
        <PlaybookSelect options={languagesOptions} />
      </PlaybookFormItem>
      */}
      {/* Optional — when unset (Auto) the backend falls back to its
          configured default, so PODCAST_DEFAULTS seeds no value. */}
      {hideTargetDuration ? null : (
        <PlaybookFormItem
          name={["generation_settings", "target_duration_in_s"]}
          labelKey="createExperience.addOns.podcast.targetDuration.label"
        >
          <PlaybookSelect
            options={durations}
            allowClear
            placeholder={t(
              "createExperience.addOns.podcast.targetDuration.placeholder"
            )}
          />
        </PlaybookFormItem>
      )}
      <PlaybookFormItem
        name={["functional_settings", "playback_speed_control"]}
        labelKey="createExperience.addOns.podcast.playbackSpeedControl"
        valuePropName="checked"
      >
        <Toggle size="l" />
      </PlaybookFormItem>
      <PlaybookFormItem
        name={["functional_settings", "auto_play_on_open"]}
        labelKey="createExperience.addOns.podcast.autoPlayOnOpen"
        valuePropName="checked"
      >
        <Toggle size="l" />
      </PlaybookFormItem>
    </Grid>
  );
};

export default PodcastSettingsFields;
